// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

export const GLOSSARY: Readonly<Record<string, { title: string; body: string }>> = {
  lora: {
    title: 'LoRA',
    body: 'Low-Rank Adaptation: a small file, usually 10–200 MB, that teaches an existing image model one new subject, style or character. You train it on a few dozen photographs rather than retraining the whole model, which is why the quality of those few dozen photographs decides almost everything.',
  },
  dhash: {
    title: 'Perceptual hash (dHash)',
    body: 'A 64-bit fingerprint of how brightness changes across an image. Shrink to a 9×8 grid, then record one bit per pair of side-by-side pixels: is the right one brighter? Re-encoding, resizing and a brightness change barely move it, so two copies of the same shot land on nearly the same fingerprint. A mirror flip or a 90° rotation moves it as far as a completely unrelated photograph does.',
  },
  hamming: {
    title: 'Hamming distance',
    body: 'How many of the 64 fingerprint bits differ. Measured on a real photograph: a JPEG re-encode is 1 bit, a downscale to a quarter size is 1, a 6-pixel blur is 1, cropping 5% off each edge is 14, and an unrelated image is about 31. Lower means more similar.',
  },
  embedding: {
    title: 'Embedding',
    body: 'A list of 512 numbers describing what is IN a picture rather than how its pixels are arranged. Two photographs of the same dog from different angles have similar embeddings even though their perceptual hashes are nothing alike. Loraprep computes them on your machine with a small vision model.',
  },
  cosine: {
    title: 'Cosine similarity',
    body: 'How closely two embeddings point in the same direction, from −1 to 1. Around 0.1 for unrelated photographs, above 0.9 for two frames of the same moment. The slider sets where you draw the line.',
  },
  laplacian: {
    title: 'Sharpness (variance of the Laplacian)',
    body: 'A measure of how much fine edge detail an image has. The absolute number means nothing on its own — a sharp photograph of a clear sky scores lower than a badly focused photograph of a brick wall — and it also RISES as JPEG quality falls, because blocking artefacts are high-frequency energy too. Loraprep uses it only to tell which frames in a group are genuinely in focus, then picks the least-compressed of those.',
  },
  'single-linkage': {
    title: 'Single linkage',
    body: 'How the groups are formed: if A matches B and B matches C, all three end up in one group even when A and C do not match each other. Across a slow pan this can chain further than you would like, which is why every group is shown to you before anything is exported.',
  },
  repeats: {
    title: 'Repeats',
    body: 'The number in a folder name like 10_mydog. kohya_ss reads it as "use each of these images 10 times per epoch", which is how you balance a small subject set against a larger set of regularisation images.',
  },
  'trigger-word': {
    title: 'Trigger word',
    body: 'The rare token you put first in every caption — sks, ohwx, or a made-up word — so that typing it in a prompt later summons your subject. It should be a word the base model has no strong existing idea about.',
  },
  'concept-folder': {
    title: 'Concept folder',
    body: 'kohya_ss splits a folder name on the first underscore: the part before it is the repeat count, the part after is the class token. A folder whose name does not start with a number is skipped with a warning, which is a common way to end up training on nothing.',
  },
  webgpu: {
    title: 'WebGPU',
    body: "The browser's modern access to the graphics card. Where it works it makes the vision model several times faster. Loraprep tries it first, then checks the result against a known-correct answer stored in its own source, because a graphics backend that quietly returns wrong numbers is worse than a slow one.",
  },
};

/**
 * Click-to-define tooltips.
 *
 * Dismissal follows the same contract as every other overlay: Escape, a pointer anywhere outside,
 * and re-clicking the term itself. One event family only.
 */
export function initGlossary(tooltip: HTMLElement): void {
  let current: HTMLElement | null = null;

  const hide = (): void => {
    tooltip.setAttribute('hidden', '');
    current = null;
  };

  document.addEventListener('click', (e) => {
    const target = (e.target as HTMLElement).closest<HTMLElement>('.glossary-link[data-term]');
    if (!target) {
      if (current && !tooltip.contains(e.target as Node)) hide();
      return;
    }
    const term = target.dataset['term'];
    const entry = term ? GLOSSARY[term] : undefined;
    if (!entry) return;
    if (current === target) {
      hide();
      return;
    }
    e.preventDefault();
    tooltip.replaceChildren();
    const h = document.createElement('strong');
    h.textContent = entry.title;
    tooltip.append(h, document.createTextNode(entry.body));
    tooltip.removeAttribute('hidden');
    const r = target.getBoundingClientRect();
    const width = Math.min(340, window.innerWidth - 24);
    tooltip.style.width = `${width}px`;
    tooltip.style.left = `${Math.max(12, Math.min(r.left, window.innerWidth - width - 12))}px`;
    const below = r.bottom + 8;
    tooltip.style.top =
      below + tooltip.offsetHeight > window.innerHeight
        ? `${Math.max(12, r.top - tooltip.offsetHeight - 8)}px`
        : `${below}px`;
    current = target;
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && current) hide();
  });
  window.addEventListener('resize', hide);
}
